/**
 * The prop catalogue — ToyRaiders' entire creative vocabulary lives here.
 *
 * Single source of truth: the builder palette, the legality checker, the
 * physics sim and the renderer all read these same entries, so what the owner
 * places, what the rules allow and what the raider hits can never disagree.
 *
 * Design rule for this table: **every prop must be a sentence about the person
 * who will walk into it**, not a level-design primitive. A wall is only
 * interesting because it suggests where you *should* go instead.
 */

export type PropTypeId =
  | 'brick'
  | 'bluff'
  | 'spring'
  | 'swatter'
  | 'marbles'
  | 'glue'
  | 'fan'
  | 'pit'
  | 'decoy'

/** How the prop reads in the room: a solid thing, something on the floor, or a machine. */
export type PropKind = 'block' | 'floor' | 'machine'

export type TrapBehavior =
  /** Pure theatre. Looks lethal, does nothing. The heart of the mind-game. */
  | { type: 'none' }
  /** Fires the raider along its facing direction. */
  | { type: 'launch'; power: number; up: number }
  /** A timed arm that sweeps sideways across the cell in front of it. */
  | { type: 'sweep'; period: number; power: number }
  /** Low-friction floor: momentum keeps you going where you didn't want to go. */
  | { type: 'slip'; friction: number }
  /** Sticky floor: costs you the escape timer, not your body. */
  | { type: 'glue'; slow: number }
  /** Constant wind over the cells in front. */
  | { type: 'push'; power: number; reach: number }
  /** Hole in the floor: fall through, wake up at the door, keep the shame. */
  | { type: 'pit' }

export interface PropType {
  id: PropTypeId
  /** Palette label. */
  name: string
  /** One line, addressed to the builder, about what it does to a *person*. */
  hint: string
  /** Build-budget cost. Cheap props are the ones you bluff with. */
  cost: number
  kind: PropKind
  /** Does it stop movement? Used by the solvability check and the sim. */
  blocking: boolean
  /** Visual + collider height, in cells. */
  height: number
  /** Toon body colour. */
  color: number
  /** Accent colour (stripes, spikes, dial). */
  accent: number
  /** Whether rotation is meaningful (a spring aims; a brick does not). */
  directional: boolean
  behavior: TrapBehavior
  /** Per-room cap, so a room can't be one trap spammed 40 times. */
  maxPerRoom: number
}

export const PROP_TYPES: Record<PropTypeId, PropType> = {
  brick: {
    id: 'brick',
    name: '积木墙',
    hint: '不伤人，只负责让他相信只有一条路能走。',
    cost: 1,
    kind: 'block',
    blocking: true,
    height: 1.1,
    color: 0xe8a15c,
    accent: 0xf6d2a3,
    directional: false,
    behavior: { type: 'none' },
    maxPerRoom: 40,
  },
  bluff: {
    id: 'bluff',
    name: '假陷阱板',
    hint: '一块画满尖刺的空板。他绕开它的那一步，才是你真正要抓的。',
    cost: 1,
    kind: 'floor',
    blocking: false,
    height: 0.08,
    color: 0xd8524b,
    accent: 0xfaf0dc,
    directional: false,
    behavior: { type: 'none' },
    maxPerRoom: 12,
  },
  spring: {
    id: 'spring',
    name: '弹射板',
    hint: '朝着箭头把人整个弹出去。放在他最有把握的那一格。',
    cost: 3,
    kind: 'machine',
    blocking: false,
    height: 0.18,
    color: 0x5aa9c4,
    accent: 0xffd166,
    directional: true,
    behavior: { type: 'launch', power: 13, up: 7.5 },
    maxPerRoom: 6,
  },
  swatter: {
    id: 'swatter',
    name: '苍蝇拍',
    hint: '按节奏横扫身前一格。会算拍子的人能过，冲动的人过不去。',
    cost: 3,
    kind: 'machine',
    blocking: true,
    height: 1.0,
    color: 0x8f6fc4,
    accent: 0xffd166,
    directional: true,
    behavior: { type: 'sweep', period: 2.2, power: 11 },
    maxPerRoom: 6,
  },
  marbles: {
    id: 'marbles',
    name: '弹珠地',
    hint: '不拦人，只删掉刹车。搭在弹射板前面最坏。',
    cost: 2,
    kind: 'floor',
    blocking: false,
    height: 0.06,
    color: 0x7fc8a9,
    accent: 0xffffff,
    directional: false,
    behavior: { type: 'slip', friction: 0.04 },
    maxPerRoom: 10,
  },
  glue: {
    id: 'glue',
    name: '胶水',
    hint: '不打飞他，只吃掉他的撤离时间。抱着宝箱的人最怕这个。',
    cost: 2,
    kind: 'floor',
    blocking: false,
    height: 0.05,
    color: 0xd9c14e,
    accent: 0xfff3b0,
    directional: false,
    behavior: { type: 'glue', slow: 0.42 },
    maxPerRoom: 10,
  },
  fan: {
    id: 'fan',
    name: '电风扇',
    hint: '把人往前推三格。他以为自己在走直线。',
    cost: 3,
    kind: 'machine',
    blocking: true,
    height: 1.0,
    color: 0x6b7fd7,
    accent: 0xe8f4ff,
    directional: true,
    behavior: { type: 'push', power: 9, reach: 3 },
    maxPerRoom: 5,
  },
  pit: {
    id: 'pit',
    name: '地板洞',
    hint: '掉下去的人回到门口，什么都没丢，只丢了面子和时间。',
    cost: 4,
    kind: 'floor',
    blocking: true,
    height: 0.02,
    color: 0x2f2a3a,
    accent: 0x554b6b,
    directional: false,
    behavior: { type: 'pit' },
    maxPerRoom: 4,
  },
  decoy: {
    id: 'decoy',
    name: '诱饵宝箱',
    hint: '和真宝箱一模一样，里面什么都没有。他一定会先跑向最近的那个。',
    cost: 2,
    kind: 'floor',
    blocking: false,
    height: 0.45,
    color: 0xb98a52,
    accent: 0xffd166,
    directional: false,
    behavior: { type: 'none' },
    maxPerRoom: 3,
  },
}

export const PROP_ORDER: PropTypeId[] = [
  'brick',
  'bluff',
  'spring',
  'swatter',
  'marbles',
  'glue',
  'fan',
  'pit',
  'decoy',
]

export function isPropTypeId(v: unknown): v is PropTypeId {
  return typeof v === 'string' && v in PROP_TYPES
}

export function propType(id: PropTypeId): PropType {
  return PROP_TYPES[id]
}
